import { useCallback, useEffect, useState } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Chip, CircularProgress, Grid, IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import { kyndrylColors } from '../../theme/kyndrylTheme';

/**
 * Namespaces to aggregate pod counts from.
 * Add entries here to extend coverage.
 */
const WATCHED_NAMESPACES = ['backstage', 'agents', 'modernization-factory'];

interface PodCounts {
  running: number;
  pending: number;
  failed: number;
}

const useStyles = makeStyles(theme => ({
  loading: { display: 'flex', justifyContent: 'center', padding: theme.spacing(3) },
  error: { padding: theme.spacing(2), color: theme.palette.error.main, fontSize: '0.85rem' },
  statChip: { fontWeight: 700, fontSize: '0.9rem', padding: theme.spacing(0, 1) },
  label: { fontSize: '0.7rem', color: theme.palette.text.secondary, marginTop: theme.spacing(0.5), textAlign: 'center' },
  statBlock: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  nsSection: { marginTop: theme.spacing(1) },
  nsLabel: { fontSize: '0.7rem', color: theme.palette.text.secondary, fontFamily: 'monospace' },
}));

export const PlatformHealthCard = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [counts, setCounts] = useState<PodCounts>({ running: 0, pending: 0, failed: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
      const totals: PodCounts = { running: 0, pending: 0, failed: 0 };

      await Promise.all(
        WATCHED_NAMESPACES.map(async ns => {
          try {
            const resp = await fetchApi.fetch(
              `${baseUrl}/proxy/api/v1/namespaces/${encodeURIComponent(ns)}/pods`,
              { headers: { 'Backstage-Kubernetes-Cluster': 'aks' } },
            );
            if (!resp.ok) return;
            const data = await resp.json();
            const items: { status?: { phase?: string } }[] = data.items ?? [];
            for (const pod of items) {
              const phase = (pod.status?.phase ?? '').toLowerCase();
              if (phase === 'running') totals.running++;
              else if (phase === 'pending') totals.pending++;
              else if (phase === 'failed' || phase === 'crashloopbackoff') totals.failed++;
            }
          } catch {
            // namespace may not exist in dev — skip silently
          }
        }),
      );

      setCounts(totals);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch pod data');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const action = (
    <Tooltip title="Refresh">
      <span>
        <IconButton size="small" onClick={fetchCounts} disabled={loading}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <InfoCard
      title="Platform Health"
      subheader={`Namespaces: ${WATCHED_NAMESPACES.join(', ')}`}
      action={action}
    >
      {loading ? (
        <div className={classes.loading}><CircularProgress size={24} /></div>
      ) : null}
      {!loading && error ? (
        <Typography className={classes.error}>{error}</Typography>
      ) : null}
      {!loading && !error ? (
        <Grid container spacing={2} justifyContent="center">
          <Grid item className={classes.statBlock}>
            <Chip label={counts.running} className={classes.statChip} style={{ backgroundColor: kyndrylColors.digitalDarkSpringGreen, color: '#fff' }} />
            <Typography className={classes.label}>Running</Typography>
          </Grid>
          <Grid item className={classes.statBlock}>
            <Chip label={counts.pending} className={classes.statChip} style={{ backgroundColor: kyndrylColors.earth, color: '#fff' }} />
            <Typography className={classes.label}>Pending</Typography>
          </Grid>
          <Grid item className={classes.statBlock}>
            <Chip label={counts.failed} className={classes.statChip} style={{ backgroundColor: kyndrylColors.warmRed, color: '#fff' }} />
            <Typography className={classes.label}>Failed</Typography>
          </Grid>
        </Grid>
      ) : null}
    </InfoCard>
  );
};
